import { groupByUnStr } from '../../api/customer/analytics.api';
import { roundDecimals } from '../../fromKotlin/nkutils';
import { fixColumnsWidth } from './ag-table-utils';
import { ITableControls } from './interfaces';

export function extractBeforeNumber(input: string): string {
    const regex = /^([a-zA-Z0-9]+(?:_[a-zA-Z]+)*?)_\d/;
    const match = input.match(regex);
    return (match ? match[1] : null) ?? input;
}

/** Product id from grid/export column `p_123` or `rrp_123`. */
export const productIdFromQtyColId = (colId: string): string | null => {
    if (typeof colId !== 'string') return null;
    const m = colId.match(/^(?:rrp_|p_)(.+)$/);
    return m ? m[1] : null;
};

export const isProductQtyColId = (colId: string): boolean => productIdFromQtyColId(colId) != null;

/** Row data key for bundle source qty (always `p_{id}`). */
export const productQtyDataKey = (productId: string): string => `p_${productId}`;

/** Grid column id for a product qty cell — prefers `rrp_*` when present (e.g. Pommeranz). */
export const resolveProductQtyGridColId = (productId: string, gridColIds: string[]): string | null => {
    const rrp = `rrp_${productId}`;
    const p = `p_${productId}`;
    if (gridColIds.includes(rrp)) return rrp;
    if (gridColIds.includes(p)) return p;
    return null;
};

export const bundleAdditionForProductQtyCol = (
    additions: Record<string, number> | null | undefined,
    colId: string
): number => {
    const pid = productIdFromQtyColId(colId);
    if (!pid || !additions) return 0;
    const add = additions[productQtyDataKey(pid)];
    return add && !Number.isNaN(add) ? add : 0;
};

const bundleSourceAmount = (data: any, srcId: string): number => {
    let amount = Number(data[productQtyDataKey(srcId)]);
    if (!amount || Number.isNaN(amount)) {
        amount = Number(data[`rrp_${srcId}`]);
    }
    return amount;
};

/** Same bundle semantics as Excel export: `aap_{sourceId}` → extra amounts on `p_*` destination columns. */
export const getBundleAdditionsMap = (data: any): Record<string, number> | null => {
    if (!data || typeof data !== 'object') return null;
    let result: Record<string, number> | null = null;
    for (const key in data) {
        if (!key || key.indexOf('aap_') !== 0) continue;
        const bundleValue = data[key];
        if (bundleValue == null || bundleValue === '') continue;
        const bundleStr = typeof bundleValue === 'string' ? bundleValue : String(bundleValue);
        if (!bundleStr.trim()) continue;
        const srcId = key.substring(4);
        const amount = bundleSourceAmount(data, srcId);
        if (!amount || Number.isNaN(amount)) continue;
        const ids = bundleStr.split('_').map((s: string) => s.trim()).filter(Boolean);
        for (const id of ids) {
            const target = productQtyDataKey(id);
            if (!result) result = {};
            result[target] = (result[target] || 0) + amount;
        }
    }
    return result;
};

const bundleExtraForColumn = (row: any, colKey: string): number =>
    bundleAdditionForProductQtyCol(getBundleAdditionsMap(row), colKey);

/** Per-row product qty cell total (base value + bundle destinations for this column). */
export const productQtyRowCellTotal = (row: any, colId: string, valueGetter?: (d: any) => any): number => {
    const raw = valueGetter ? valueGetter(row) : row?.[colId];
    let n = typeof raw === 'number' ? raw : Number(raw);
    if (Number.isNaN(n)) n = 0;
    return n + bundleExtraForColumn(row, colId);
};

/** Sum a product column across rows, including bundle (`aap_*`) roll-ups. */
export const sumProductQtyColWithBundle = (
    rowData: any[],
    colId: string,
    valueGetter?: (d: any) => any
): number => {
    let sum = 0;
    rowData?.forEach((item) => {
        sum += productQtyRowCellTotal(item, colId, valueGetter);
    });
    return sum;
};

export const saveColumnState = (params, tableControls, gridReady, force_vis?) => {
    if (!gridReady) return;
    if (force_vis || tableControls?.groupBy?.allowSave || tableControls?.defaultGroupBy) {
    }

    const columnState = params?.columnApi?.getColumnState();
    const stateToSave = columnState?.map((column, i) => {
        const forceV = force_vis?.find((col) => col.colId === column?.colId);
        return {
            ...column,
            colId: extractBeforeNumber(column?.colId) ?? column?.colId,
            hide: forceV ? true : column?.hide
        };
    });
    localStorage.setItem(tableControls.tableName + '-columnsState', JSON.stringify(stateToSave));
    const groupState = params?.columnApi?.getColumnGroupState();
    localStorage.setItem(tableControls.tableName + '-columnsStategrp', JSON.stringify(groupState));
};

type RowPinned = 'top' | 'bottom' | null;

type GridFocusSnap = {
    rowIndex: number;
    colId: string;
    rowPinned: RowPinned;
    /** Row node id, so a transaction that shifts rows still restores the same row. */
    rowId?: string | null;
};

const pinnedOf = (value: unknown): RowPinned => (value === 'top' || value === 'bottom' ? value : null);

function rowCountFor(api: any, rowPinned: RowPinned): number {
    if (rowPinned === 'top') return api?.getPinnedTopRowCount?.() ?? 0;
    if (rowPinned === 'bottom') return api?.getPinnedBottomRowCount?.() ?? 0;
    return api?.getDisplayedRowCount?.() ?? 0;
}

function rowNodeAt(api: any, rowIndex: number, rowPinned: RowPinned) {
    if (rowPinned === 'top') return api?.getPinnedTopRow?.(rowIndex) ?? null;
    if (rowPinned === 'bottom') return api?.getPinnedBottomRow?.(rowIndex) ?? null;
    return api?.getDisplayedRowAtIndex?.(rowIndex) ?? null;
}

// Matched on the row's row-index attribute: the cell's col-id attribute is HTML-escaped.
function browserFocusInRow(rowIndex: number, rowPinned: RowPinned): boolean {
    if (typeof document === 'undefined') return false;
    const cell = (document.activeElement as HTMLElement | null)?.closest?.('.ag-cell');
    const rowAttr = cell?.closest('.ag-row')?.getAttribute('row-index');
    if (rowAttr == null) return false;
    const prefix = rowPinned === 'top' ? 't-' : rowPinned === 'bottom' ? 'b-' : '';
    return rowAttr === `${prefix}${rowIndex}`;
}

/** The focused cell, only while the browser focus is on it. getFocusedCell keeps a cell long after the user left the grid. */
export function captureGridFocus(api: any): GridFocusSnap | null {
    try {
        const focused = api?.getFocusedCell?.();
        if (!focused || focused.rowIndex == null) return null;
        const colId = focused.column?.getColId?.() ?? focused.column?.colId;
        if (!colId) return null;
        const rowPinned = pinnedOf(focused.rowPinned);
        if (!browserFocusInRow(focused.rowIndex, rowPinned)) return null;
        const node = rowNodeAt(api, focused.rowIndex, rowPinned);
        return {
            rowIndex: focused.rowIndex,
            colId: String(colId),
            rowPinned,
            rowId: node?.id != null ? String(node.id) : null,
        };
    } catch {
        return null;
    }
}

/** Put keyboard focus back on a cell after a refresh dropped it onto document.body. */
export function restoreGridFocus(api: any, snap: GridFocusSnap | null) {
    if (!api || !snap || typeof document === 'undefined') return;
    // Focus anywhere but the page body was put there by the user: a filter, a dialog, another grid.
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    try {
        if ((api.getEditingCells?.() ?? []).length > 0) return;
        if (!api.getColumnDef?.(snap.colId)) return;
        // A row re-added under a new id (blank row that got an order) sits at the old index.
        const node = snap.rowPinned == null && snap.rowId != null ? api.getRowNode?.(snap.rowId) : null;
        const rowIndex = node ? node.rowIndex : snap.rowIndex;
        if (rowIndex == null || rowIndex < 0 || rowIndex >= rowCountFor(api, snap.rowPinned)) return;
        api.setFocusedCell(rowIndex, snap.colId, snap.rowPinned);
    } catch {
        // grid destroyed, or the row / column went away with the update
    }
}

const focusRestoreHandles = new WeakMap<object, {timeout: number; raf: number}>();

export function scheduleGridFocusRestore(api: any, snap: GridFocusSnap | null) {
    if (!api || !snap || typeof window === 'undefined') return;
    restoreGridFocus(api, snap);
    const prev = focusRestoreHandles.get(api);
    if (prev) {
        window.clearTimeout(prev.timeout);
        window.cancelAnimationFrame(prev.raf);
    }
    // applyTransaction paints the new row on the next animation frame and drops the
    // cell that Enter / arrows just moved to. Restore after that frame, then once more
    // for the React cell renderer commit.
    const raf = window.requestAnimationFrame(() => restoreGridFocus(api, snap));
    const timeout = window.setTimeout(() => {
        focusRestoreHandles.delete(api);
        restoreGridFocus(api, snap);
    }, 50);
    focusRestoreHandles.set(api, {timeout, raf});
}

/**
 * Force-refresh rows (values and cellStyle) without redrawRows, which rebuilds the row and drops
 * keyboard focus. Focus is put back if the transaction that came with the refresh dropped it.
 */
export function refreshRowsPreservingFocus(api: any, nodes: any[], snap: GridFocusSnap | null = captureGridFocus(api)) {
    const rowNodes = (nodes ?? []).filter(Boolean);
    if (!api || rowNodes.length === 0) return;
    api.refreshCells({rowNodes, force: true});
    scheduleGridFocusRestore(api, snap);
}

export const onCellKeyDown = (params: any, tableRef: any, menuiItems, enterMovesDown?: boolean) => {
    const isMultipleSelection = menuiItems?.[params?.colDef?.id];
    if (isMultipleSelection) {
        return;
    }

    const editable = params?.colDef?.editable;
    if (!editable) {
        return;
    }

    const key = params?.event?.key;
    const rowIndex = params?.rowIndex;
    const column = params?.column;
    let nextRowIndex;
    let nextColumn;
    switch (key) {
        case 'ArrowUp':
            nextRowIndex = rowIndex - 1;
            nextColumn = column;
            break;
        case 'ArrowDown':
            nextRowIndex = rowIndex + 1;
            nextColumn = column;
            break;
        case 'ArrowRight':
            nextRowIndex = rowIndex;
            nextColumn = tableRef?.current?.columnApi?.getDisplayedColBefore(column);
            break;
        case 'ArrowLeft':
            nextRowIndex = rowIndex;
            nextColumn = tableRef?.current?.columnApi?.getDisplayedColAfter(column);
            break;
        case 'Enter':
            if (enterMovesDown) {
                params?.event?.preventDefault?.();
                nextRowIndex = rowIndex + 1;
                nextColumn = column;
            } else {
                nextRowIndex = rowIndex;
                nextColumn = tableRef?.current?.columnApi?.getDisplayedColAfter(column);
            }
            break;
        default:
            return;
    }
    const api = tableRef?.current?.api;
    const rowPinned = pinnedOf(params?.rowPinned);
    api?.stopEditing();
    const nextColId = nextColumn == null
        ? null
        : typeof nextColumn === 'string' ? nextColumn : (nextColumn.getColId?.() ?? nextColumn.colId);
    // No neighbour (edge column, first or last row): focusing it clears the grid's focused cell
    // and keyboard navigation stops until a mouse click. Stay on the current cell instead.
    if (nextColId == null || nextRowIndex == null || nextRowIndex < 0 || nextRowIndex >= rowCountFor(api, rowPinned)) {
        const colId = column?.getColId?.() ?? column?.colId;
        if (colId != null && rowIndex != null) {
            scheduleGridFocusRestore(api, {rowIndex, colId: String(colId), rowPinned});
        }
        return;
    }
    api?.setFocusedCell(nextRowIndex, nextColId, rowPinned);
    // stopEditing removes the editor input. That blur can land after this handler and
    // leave the browser on document.body while the grid still thinks a cell is focused.
    scheduleGridFocusRestore(api, {rowIndex: nextRowIndex, colId: String(nextColId), rowPinned});
    if (enterMovesDown) {
        setTimeout(() => {
            tableRef?.current?.api?.startEditingCell({
                rowIndex: nextRowIndex,
                colKey: nextColId,
                rowPinned: rowPinned ?? undefined,
            });
        }, 0);
    }
    // tableRef?.current?.api?.startEditingCell({
    //   rowIndex: nextRowIndex,
    //   colKey: nextColumn?.colId,
    // });
};

export const onColumnMoved = (params: any, tableControls: ITableControls, gridReady) => {
    if (params.finished && params?.source !== 'api' && params?.source !== 'gridOptionsChanged') {
        saveColumnState(params, tableControls, gridReady);
    }
};
export const onColumnGroupOpened = (params: any, tableControls: ITableControls, gridReady) => {
    saveColumnState(params, tableControls, gridReady);
};

export const onColumnResized = (params: any, tableControls: ITableControls, gridReady) => {
    if (
        params.finished &&
        (params?.source === 'uiColumnDragged' || params?.source === 'autosizeColumns' || params?.source === 'uiColumnResized')
    ) {
        saveColumnState(params, tableControls, gridReady);
    }
};

export const onColumnPinned = (params: any, tableControls: ITableControls, gridReady) => {
    saveColumnState(params, tableControls, gridReady);
};

export const addDragToTable = (params) => {
    const dragColumn = {
        headerName: '',
        field: 'drag',
        width: 50,
        pinned: 'right',
        rowDrag: true
    };
    const columnDefs = params.api.getColumnDefs();
    if (columnDefs.find((column) => column.field === 'drag')) {
        return;
    }
        params.api.setColumnDefs([dragColumn, ...columnDefs]);
};

export const removeDragFromTable = (params) => {
    const columnDefs = params.api.getColumnDefs();
    const newColumnDefs = columnDefs.filter((column) => column.field !== 'drag');
    params.api.setColumnDefs(newColumnDefs);
};

export const onFilterChanged = (params: any) => {
    const filterModel = params.api.getFilterModel();
    const allColumns = params.columnApi.getAllColumns();
    allColumns.forEach((column: any) => {
        const filterPresent = filterModel[column.colId] !== undefined;
        column.setFilterActive(filterPresent);
        if (filterPresent) {
            column.setColDef({
                ...column.getColDef(),
                headerClass: 'active-header-filter'
            });
        } else {
            column.setColDef({
                ...column.getColDef(),
                headerClass: ''
            });
        }
    });
};

export const onColumnVisible = (params, tableControls: ITableControls, gridReady) => {
    const changedColumn = params?.column?.colDef;
    changedColumn?.hideCallback ? changedColumn?.hideCallback(params) : null;
    saveColumnState(params, tableControls, gridReady);
};

export const createPinnedRowDataNew = (
    columns,
    rowData,
    config,
    withoutFormatter,
    /** When true (Excel export only), product sum/avg includes `aap_*` bundle roll-ups. */
    includeBundleInQtySums = false
) => {
    const obj: {
        [key: string]: any;
    } = {};
    const format = (type, value) => (type === 'currency' ? '₪ ' + value : type === 'percent' ? value + ' %' : `${value}`);
    columns.forEach((column) => {
        const key = column?.id;
        const defaultAction = config?.func?.[column.id] ?? column?.pinnedRowProps?.action;
        let defaultValueGetter = (d) => d?.[key];
        if (column?.extractFunc) {
            defaultValueGetter = (d) => column?.extractFunc(d);
        }
        if (column?.valueGetter) {
            defaultValueGetter = (d) => column?.valueGetter(d);
        }

        if (config?.valueGetter?.[column.id]) {
            defaultValueGetter = config?.valueGetter?.[column.id];
        }
        let formatter = config?.format?.[column.id] ?? column?.pinnedRowProps?.format;

        if (typeof defaultAction === 'function') {
            let v = defaultAction(rowData.map((d) => defaultValueGetter(d)));
            obj[key] = !withoutFormatter && formatter ? format(formatter, v) : v;
            return;
        }
        switch (defaultAction) {
            case 'count':
                obj[key] = rowData?.length;
                break;
            case 'dev':
                let x = 0;
                let y = 0;
                rowData?.forEach((item: any) => {
                    if (column?.extractFunc) {
                        const d = column?.extractFunc(item);
                        x += d.x;
                        y += d.y;
                    } else {
                    }
                });
                const sum = x && y ? Math.min(100, (100 * y) / x) : 0;
                const formatted = `${roundDecimals(sum)}`;
                obj[key] = !withoutFormatter && formatter && sum ? format(formatter, formatted) : formatted;
                break;
            case 'uniqueCount': {
                const unique = new Set();
                rowData?.forEach((item: any) => {
                    const v = defaultValueGetter(item);
                    if (v && v != '') unique.add(v);
                });
                obj[key] = unique.size;
                break;
            }
            case 'booleanCount': {
                let count = 0;
                rowData?.forEach((item: any) => {
                    const v = defaultValueGetter(item);
                    if (v) count++;
                });
                obj[key] = rowData?.length + ' / ' + count;
                break;
            }
            case 'avg': {
                let sum = 0;
                let count = 0;
                rowData?.forEach((item: any) => {
                    let v = defaultValueGetter(item);
                    if (typeof v !== 'number') {
                        const toNumber = Number(v);
                        v = isNaN(toNumber) ? 0 : toNumber;
                    }
                    if (includeBundleInQtySums) {
                        v = productQtyRowCellTotal(item, key, defaultValueGetter);
                    }
                    sum += v;
                    count++;
                });
                const formatted = (sum / count)?.toFixed(2)?.replace(/\d(?=(\d{3})+\.)/g, '$&,');
                if (formatted === 'NaN') {
                    obj[key] = 0;
                } else {
                    obj[key] = !withoutFormatter && formatter && sum ? format(formatter, formatted) : formatted;
                }
                break;
            }
            case 'sum': {
                if (column?.[key] === 'string') {
                    obj[key] = '';
                    break;
                }
                let sum = 0;
                if (includeBundleInQtySums) {
                    sum = sumProductQtyColWithBundle(rowData, key, defaultValueGetter);
                } else {
                    rowData?.forEach((item: any) => {
                        const v = defaultValueGetter(item);
                        let n = 0;
                        if (typeof v === 'number') {
                            n = v;
                        } else {
                            const toNumber = Number(v);
                            n = isNaN(toNumber) ? 0 : toNumber;
                        }
                        sum += n;
                    });
                }
                const decimals = typeof column?.pinnedRowProps?.decimals === 'number' ? column.pinnedRowProps.decimals : 2;
                const formatted = withoutFormatter || isNaN(sum) ? sum : `${roundDecimals(sum, decimals)}`;
                obj[key] = !withoutFormatter && formatter && sum ? format(formatter, formatted) : formatted;
                break;
            }
            case 'min': {
                let min = Infinity;
                rowData?.forEach((item: any) => {
                    const v = defaultValueGetter(item);
                    if (typeof v === 'number') {
                        min = Math.min(min, v);
                    } else {
                        const toNumber = Number(v);
                        min = Math.min(min, isNaN(toNumber) ? 0 : toNumber);
                    }
                });
                const formatted = withoutFormatter ? min : `${roundDecimals(min)}`;
                obj[key] = !withoutFormatter && formatter && min ? format(formatter, formatted) : formatted;
                break;
            }
            case 'max': {
                let max = -Infinity;
                rowData?.forEach((item: any) => {
                    const v = defaultValueGetter(item);
                    if (typeof v === 'number') {
                        max = Math.max(max, v);
                    } else {
                        const toNumber = Number(v);
                        max = Math.max(max, isNaN(toNumber) ? 0 : toNumber);
                    }
                });
                const formatted = withoutFormatter ? max : `${roundDecimals(max)}`;
                obj[key] = !withoutFormatter && formatter && max ? format(formatter, formatted) : formatted;
                break;
            }
            case 'first': {
                obj[key] = rowData?.[0]?.[key];
                break;
            }
            case 'last': {
                obj[key] = rowData?.[rowData?.length - 1]?.[key];
                break;
            }
            case 'none': {
                obj[key] = '';
                break;
            }

            case 'custome': {
                obj[key] = column?.customePinnedRowAction?.custome(rowData);
                break;
            }
            default:
                obj[key] = '';
                break;
        }
    });
    return obj;
};

/** Same pinned-row construction as Ag-Table `setPinnedRowDataCallback`, for a given row subset (e.g. one driver). */
export const buildPinnedRowBlocksFromData = (
    columns: any[],
    filterdNodeData: any[],
    pinnedControl: any,
    pinnedTopRowDataConfiguration: number[],
    pinnedBottomRowDataConfiguration: number[],
    includeBundleInQtySums = false
): { top: any[]; bottom: any[] } => {
    const top: any[] = [];
    const bottom: any[] = [];
    if (!pinnedControl?.pinnedRowControlsNew) {
        return { top, bottom };
    }

    const pushTopRow = (c: any) => {
        let r: any = {};
        if (c?.editable) {
            r = c.editableData;
            r.editable = c.editable;
        } else {
            if (c?.sumByConfigurations && pinnedControl?.pinnedRowControlsNew) {
                const conf1 = pinnedControl.pinnedRowControlsNew.find((c1: any) => c1.id === c.sumByConfigurations.ids[0]);
                const conf2 = pinnedControl.pinnedRowControlsNew.find((c1: any) => c1.id === c.sumByConfigurations.ids[1]);
                let r1: any;
                let r2: any;
                if (conf1!.editableData) {
                    r1 = conf1!.editableData;
                } else {
                    r1 = createPinnedRowDataNew(columns, filterdNodeData, conf1, true, includeBundleInQtySums);
                }
                if (conf2!.editableData) {
                    r2 = conf2!.editableData;
                } else {
                    r2 = createPinnedRowDataNew(columns, filterdNodeData, conf2, true, includeBundleInQtySums);
                }
                Object.keys(r1).forEach((k) => {
                    switch (c.sumByConfigurations.func) {
                        case 'sum':
                            if (!isNaN(Number(r1[k])) && !isNaN(Number(r2[k]))) r[k] = (Number(r1[k]) ?? 0) + (Number(r2[k]) ?? 0);
                            else r[k] = '';
                            break;
                        case 'minus':
                            if (!isNaN(Number(r1[k])) && !isNaN(Number(r2[k]))) r[k] = (Number(r1[k]) ?? 0) - (Number(r2[k]) ?? 0);
                            else r[k] = '';
                            break;
                    }
                });
                r[c.sumByConfigurations.name] = c.label;
            } else {
                if (c?.editableData) {
                    r = c.editableData;
                } else r = createPinnedRowDataNew(columns, filterdNodeData, c, false, includeBundleInQtySums);
            }
        }
        top.push(r);
    };

    const pushBottomRow = (c: any) => {
        let r: any = {};
        if (c?.editable) {
            r = c.editableData;
            r.editable = c.editable;
        } else {
            if (c?.sumByConfigurations && pinnedControl?.pinnedRowControlsNew) {
                const conf1 = pinnedControl.pinnedRowControlsNew.find((c1: any) => c1.id === c.sumByConfigurations.ids[0]);
                const conf2 = pinnedControl.pinnedRowControlsNew.find((c1: any) => c1.id === c.sumByConfigurations.ids[1]);
                let r1: any;
                let r2: any;
                if (conf1!.editableData) {
                    r1 = conf1!.editableData;
                } else {
                    r1 = createPinnedRowDataNew(columns, filterdNodeData, conf1, false, includeBundleInQtySums);
                }
                if (conf2!.editableData) {
                    r2 = conf2!.editableData;
                } else {
                    r2 = createPinnedRowDataNew(columns, filterdNodeData, conf2, false, includeBundleInQtySums);
                }
                Object.keys(r1).forEach((k) => {
                    switch (c.sumByConfigurations.func) {
                        case 'sum':
                            if (!isNaN(Number(r1[k])) && !isNaN(Number(r2[k]))) r[k] = (Number(r1[k]) ?? 0) + (Number(r2[k]) ?? 0);
                            else r[k] = '';
                            break;
                        case 'minus':
                            if (!isNaN(Number(r1[k])) && !isNaN(Number(r2[k]))) r[k] = (Number(r1[k]) ?? 0) - (Number(r2[k]) ?? 0);
                            else r[k] = '';
                            break;
                    }
                });
                r[c.sumByConfigurations.name] = c.label;
            } else {
                if (c?.editableData) {
                    r = c.editableData;
                } else r = createPinnedRowDataNew(columns, filterdNodeData, c, false, includeBundleInQtySums);
            }
        }
        bottom.push(r);
    };

    pinnedTopRowDataConfiguration
        .map((id) => pinnedControl.pinnedRowControlsNew.find((x: any) => x.id == id))
        .forEach((c) => {
            if (c) pushTopRow(c);
        });

    pinnedBottomRowDataConfiguration
        .map((id) => pinnedControl.pinnedRowControlsNew.find((x: any) => x.id == id))
        .forEach((c) => {
            if (c) pushBottomRow(c);
        });

    return { top, bottom };
};

export const onManyCellEditingStopped = (
    paramsis: any[],
    setEditMode,
    setStatusBarData,
    setValueAlert,
    setEditList,
    editList,
    editCallback,
    editCallbackMany,
    allowSame
) => {
    const arr: any = [];

    const pushIfValid = (p: any) => {
        if (p.oldValue != p.newValue || allowSame) {
            // Handle boolean string-to-bool normalization
            if (typeof p?.oldValue === 'boolean' && (p?.newValue === 'true' || p?.newValue === 'false')) {
                if ((p?.oldValue && p.newValue === 'true') || (!p?.oldValue && p.newValue === 'false')) return;
                p.node.setDataValue(p.column, p.newValue === 'true');
            }
            const columnDefs = p?.colDef;
            const columnValueLimit = columnDefs?.valueLimit;
            if (columnValueLimit && (p?.newValue > columnValueLimit?.max || p?.newValue < columnValueLimit?.min)) {
                setValueAlert(true);
                p.node.setDataValue(p.column.colId, p.oldValue);
                return;
            }
            // mark row as edited and collect
            if (p?.data) p.data.hasBeenEdited = true;
            arr.push(p);
        }
    };

    paramsis.forEach((params) => {
        // If this is a group (father) node, expand into leaf (sons) synthetic edits
        try {
            if (params?.node?.group) {
                const colId = params?.column?.getColId?.() ?? params?.column?.colId ?? params?.colDef?.id;
                const leaves = params?.node?.allLeafChildren || [];
                leaves.forEach((leaf: any) => {
                    if (!leaf?.data) return;
                    // respect per-row editability
                    const editableDef: any = params?.column?.getColDef?.() ?? params?.column?.colDef ?? params?.colDef;
                    const ed = editableDef?.editable;
                    const leafParams: any = { ...params, node: leaf, data: leaf.data, column: params.column ?? { colDef: editableDef }, colDef: editableDef };
                    const isEditableForLeaf = typeof ed === 'function' ? !!ed(leafParams) : !!ed;
                    if (!isEditableForLeaf) return;
                    const oldVal = leaf?.data?.[colId];
                    const childParams = {
                        ...params,
                        node: leaf,
                        data: leaf.data,
                        oldValue: oldVal,
                        newValue: params.newValue
                    };
                    pushIfValid(childParams);
                });
                // Do not add the group param itself
                return;
            }
        } catch (_) {
            // fall through to normal handling
        }
        pushIfValid(params);
    });

    if (arr.length === 0) {
        return;
    }
    refreshRowsPreservingFocus(arr[0]?.api, arr.map((p: any) => p.node));
    if (editCallback) {
        arr.forEach((p: any) => editCallback(p));
    }
    if (editCallbackMany) {
        editCallbackMany(arr);
    }
    setEditMode(true);
    const grp = groupByUnStr(arr, (p) => p?.node?.id);
    const editGrp = groupByUnStr(editList, (p) => p?.id);
    const newEditted: any = {};

    Object.keys(grp).forEach((g) => {
        const arr = grp[g];
        const arr0 = arr[0];
        const editted = editGrp[arr0?.node?.id]?.[0];
        const newNode: any = {
            id: arr0?.node?.id,
            ...editted
        };

        arr.forEach((p) => {
            newNode.data = {
                ...newNode.data,
                [p?.colDef?.id]: p.newValue
            };
            if (newNode.data[`${p?.colDef?.id}_old`] == undefined) {
                newNode.data[`${p?.colDef?.id}_old`] = p.oldValue;
            }
        });
        newEditted[arr0?.node?.id] = newNode;
    });
    Object.keys(editGrp).forEach((g) => {
        if (!newEditted[g]) {
            newEditted[g] = editGrp[g][0];
        }
    });
    const newLst = Object.values(newEditted);
    setEditList(newLst);
    setStatusBarData((prev) => {
        return {
            ...prev,
            editedRows: newLst.length
        };
    });
};

export const onCellEditingStopped = (
    params: any,
    setEditMode,
    statusBarData,
    setStatusBarData,
    setValueAlert,
    tableControls,
    setEditList,
    editList,
    editCallback
) => {
    if (params.oldValue == params.newValue) return;
    const editedColumn = [params?.column?.colDef?.id, params?.column?.colDef?.headerName];
    let skipCheck = false;
    if (typeof params?.oldValue === 'boolean' && (params?.newValue === 'true' || params?.newValue === 'false')) {
        if ((params?.oldValue && params.newValue === 'true') || (!params?.oldValue && params.newValue === 'false')) return;
        params.node.setDataValue(params.column, params.newValue === 'true');
        skipCheck = true;
    }
    const columnDefs = params?.colDef;
    const columnValueLimit = columnDefs?.valueLimit;
    if (params?.newValue > columnValueLimit?.max || params?.newValue < columnValueLimit?.min) {
        setValueAlert(true);
        params.node.setDataValue(params.column.colId, params.oldValue);
        return;
    }
    params.data.hasBeenEdited = true;
    params.api.refreshCells({ force: true, rowNodes: [params.node] });
    if (editCallback) {
        editCallback(params);
    }
    const isInEditList = editList.find((node: any) => node?.id === params?.node?.id);
    setEditMode(true);
    if (isInEditList) {
        return;
    } else {
        setStatusBarData({
            ...statusBarData,
            editedRows: statusBarData.editedRows + 1
        });
        const newNode = {
            ...params.node,
            data: {
                ...params.node.data,
                [params?.colDef?.id]: params.oldValue
            }
        };
        setEditList([...editList, newNode]);
    }
};

export const onValueDrag = (params, setEditMode, setStatusBarData, statusBarData, setValueAlert, tableControls, editList, setEditList) => {
    const value = params.initialValues[0];
    const numberOfEditedRows = changeRangeValue(params, setValueAlert, value, tableControls, editList, setEditList);
    setStatusBarData({
        ...statusBarData,
        editedRows: statusBarData.editedRows + numberOfEditedRows
    });
    setEditMode(true);
};

export const resetTable = (tableRef, tableControls, columnDefs, setCustomeUserState, gridReady) => {
        tableRef.current.api.setColumnDefs(columnDefs);
        tableRef?.current?.api?.deselectAll();
        tableRef?.current?.columnApi?.resetColumnState();
    setCustomeUserState(0);
    fixColumnsWidth(tableRef);
    saveColumnState(tableRef?.current, tableControls, gridReady);
    // tableRef?.current?.api?.refreshCells({ force: true });
};

export const changeRangeValue = (params, setValueAlert, value, tableControls, editList, setEditList) => {
    value = value === 'true' ? true : value === 'false' ? false : value;
    const selectedRange = params.api.getCellRanges()[0];
    const start =
        selectedRange.startRow.rowIndex > selectedRange.endRow.rowIndex ? selectedRange.endRow.rowIndex : selectedRange.startRow.rowIndex;
    const end =
        selectedRange.endRow.rowIndex > selectedRange.startRow.rowIndex ? selectedRange.endRow.rowIndex : selectedRange.startRow.rowIndex;
    const column = selectedRange.columns[0];
    const editableProp = column?.colDef?.editable;
    if (typeof editableProp !== 'function' && !editableProp) return;
    let newEditList: any[] = [];
    let numberOfEditedRows = 0;
    for (let i = start; i <= end; i++) {
        const rowNode = params.api.getDisplayedRowAtIndex(i);
        // per-row editable check
        const isEditableForRow = typeof editableProp === 'function'
            ? !!editableProp({ ...params, node: rowNode, data: rowNode?.data, column, colDef: column?.colDef })
            : !!editableProp;
        if (!isEditableForRow) continue;
        value = typeof rowNode.data[column.colDef.id] === 'number' && value.toString().match(/^[0-9]+$/) ? Number(value) : value;
        const columnValueLimit = column?.colDef?.valueLimit;
        if (
            valueCheck(setValueAlert, rowNode.data[column.colDef.id], value, columnValueLimit, [column.colDef.id, column.colDef.headerName])
        ) {
            params?.node?.setDataValue(params.column.colId, rowNode.data[column.colDef.id]);
            return;
        }
        if (rowNode.data?.[column.colDef.id] === value) continue;
        const isInEditList = editList.find((node: any) => node?.id === rowNode?.id);
        if (!isInEditList) {
            !rowNode.data?.hasBeenEdited && numberOfEditedRows++;
            rowNode.data.hasBeenEdited = true;
            newEditList.push({
                ...rowNode,
                data: {
                    ...rowNode.data,
                    [column.colDef.id]: rowNode.data[column.colDef.id]
                }
            });
        }
        rowNode.data[column.colDef.id] = value;
        params.api.refreshCells({ force: true });
    }
    setEditList([...editList, ...newEditList]);
    return numberOfEditedRows;
};

export const openColumnMenuOnRightClick = (event, tableRef) => {
    if (!event?.target?.className || typeof event?.target?.className !== 'string') return;
    if (event?.target?.className?.includes('header') || event?.target?.parentElement?.className?.includes('header')) {
        event?.preventDefault();
        const colId = event?.target?.innerText?.toLowerCase();
        const column = tableRef?.current?.columnApi?.getColumns()?.find((column) => column?.colDef?.headerName === colId);
        tableRef?.current?.api?.showColumnMenuAfterMouseClick(column, event);
    }
};

export const valueCheck = (setValueAlert, originValue, newValue, columnValueLimit?, columnDet?) => {
    if (typeof originValue === 'boolean' && typeof newValue !== 'boolean') {
        setValueAlert(true);
        return true;
    }
    if (typeof originValue === 'number' && !newValue.toString().match(/^[0-9]+$/)) {
        setValueAlert(true);
        return true;
    }
    if (typeof originValue === 'string' && typeof newValue !== 'string') {
        setValueAlert(true);
        return true;
    }
    if (columnValueLimit && (newValue > columnValueLimit.max || newValue < columnValueLimit.min)) {
        setValueAlert(true);
        return true;
    }
    return false;
};
